import React, { memo } from 'react';
import classnames from 'classnames';
import EntitiesMetricsCompareTableHeader from './components/EntitiesMetricsCompareTableHeader';
import EntitiesMetricsSummaryBar from './components/EntitiesMetricsSummaryBar';
import {
  EntitiesDoubleMetricsCompareTableBody,
  EntitiesMultiMetricsCompareTableBody,
} from './components/EntitiesMetricsCompareTableBody';
import type { IBaseProps, ICommonActionRes } from '@/pages/intelligence/types/common';
import type { IEntityFieldsMapping, IEntityMetricFieldsMapping } from './types';
import './index.scss';

interface IEntitiesMetricsCompareTableProps extends IBaseProps {
  // 实体数据，即横向列，比如游戏
  entities: any[];
  // 实体字段名映射
  entityFieldsMapping: IEntityFieldsMapping;
  // 时间段数据，即纵向行，比如Last Day、Last Month、Last 3 Month等
  periods: string[];
  // 时间段名映射
  periodsNamesMapping: {
    [key: string]: string;
  };
  // 指标数据 即单元格内的行，当前游戏，当前时间段下的 指标数据
  metrics: string[];
  // 指标名映射
  metricsNamesMapping: {
    [key: string]: string;
  };
  // 指标名tooltip气泡内容映射
  metricsNamesTooltipMapping: {
    [key: string]: string;
  };
  // 存储指标的map，以实体key、时间段、指标key为key存储，方便在多维表格单元格中 获取指标数据
  metricsMap: {
    [key: string]: any;
  };
  // 指标字段名映射
  metricFieldsMapping: IEntityMetricFieldsMapping;
  // 默认指标value 的格式化方法
  defaultMetricValueFormatter?: (value: any) => any;
  // 指标value 格式化方法map，以指标key作为key，可以针对不同指标配置 不同的格式化方法
  metricValueFormattersMapping?: {
    [key: string]: (value: any) => any;
  };
  // 时间段最大值指标
  periodMaxMetricsMap: {
    [key: string]: any;
  };
  // 是否开启Heat Map模式，即高亮最优指标
  isOpenHeatMap?: boolean;
  // 是否展示每个指标前3个国家的数据
  isShowTopCountries?: boolean;
  // 横向列头的render方法，即自定义渲染表格header
  headerColumnRenderer: (entity: any, columnIndex: number) => React.ReactElement | null;
  // 点击添加实体的回调
  onAdd?: (e: any) => void;
  // 删除实体的回调
  onDelete?: (entity: any, columnIndex: number, e: React.SyntheticEvent<HTMLSpanElement>) => Promise<ICommonActionRes>;
  // 是否仅按单行指标模式展示
  isOnlySingleMetricLine?: boolean;
  // 空字符占位符
  placeholder?: string;
}

const EntitiesMetricsCompareTable: React.FC<IEntitiesMetricsCompareTableProps> = (props) => {
  const {
    entities,
    entityFieldsMapping,
    periods,
    periodsNamesMapping,
    metrics,
    metricsNamesMapping,
    metricsNamesTooltipMapping,
    metricsMap,
    metricFieldsMapping,
    defaultMetricValueFormatter,
    metricValueFormattersMapping,
    periodMaxMetricsMap,
    isOpenHeatMap = false,
    isShowTopCountries = false,
    headerColumnRenderer,
    onAdd,
    onDelete,
    isOnlySingleMetricLine = false,
    placeholder = '-',
    style,
    className,
  } = props;

  return (
    <div style={style} className={classnames('dbi-entities-metrics-compare-table', className)}>
      <EntitiesMetricsCompareTableHeader
        placeholder={placeholder}
        entities={entities}
        entityFieldsMapping={entityFieldsMapping}
        columnRenderer={headerColumnRenderer}
        onAdd={onAdd}
        onDelete={onDelete}
      />
      {!isOnlySingleMetricLine && metrics?.length > 0 && metrics?.length <= 2 && (
        <>
          <EntitiesMetricsSummaryBar
            columns={entities?.length}
            metrics={metrics}
            metricsNamesMapping={metricsNamesMapping}
            metricsNamesTooltipMapping={metricsNamesTooltipMapping}
            showKeyMetricsWhenOnlyOne={false}
          />
          <EntitiesDoubleMetricsCompareTableBody
            entities={entities}
            entityFieldsMapping={entityFieldsMapping}
            periods={periods}
            periodsNamesMapping={periodsNamesMapping}
            metrics={metrics}
            metricsMap={metricsMap}
            metricFieldsMapping={metricFieldsMapping}
            defaultMetricValueFormatter={defaultMetricValueFormatter}
            metricValueFormattersMapping={metricValueFormattersMapping}
            periodMaxMetricsMap={periodMaxMetricsMap}
            isOpenHeatMap={isOpenHeatMap}
            showTopCountries={isShowTopCountries}
          />
        </>
      )}

      {(isOnlySingleMetricLine || metrics?.length > 2) && (
        <EntitiesMultiMetricsCompareTableBody
          entities={entities}
          entityFieldsMapping={entityFieldsMapping}
          periods={periods}
          periodsNamesMapping={periodsNamesMapping}
          metrics={metrics}
          metricsNamesMapping={metricsNamesMapping}
          metricsNamesTooltipMapping={metricsNamesTooltipMapping}
          metricsMap={metricsMap}
          metricFieldsMapping={metricFieldsMapping}
          defaultMetricValueFormatter={defaultMetricValueFormatter}
          metricValueFormattersMapping={metricValueFormattersMapping}
          periodMaxMetricsMap={periodMaxMetricsMap}
          isOpenHeatMap={isOpenHeatMap}
          showTopCountries={isShowTopCountries}
        />
      )}
    </div>
  );
};

export default memo(EntitiesMetricsCompareTable);
